// src/pages/notifications/NotificationsPage.jsx
import React, { useEffect, useState, useRef } from "react";
import "./NotificationsPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL}`;

/**
 * Helper to build API headers (includes Bearer token if present)
 */
function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/**
 * Try to determine current user's id:
 * 1) use localStorage.userId if set (preferred)
 * 2) try to decode localStorage.token (JWT) and read sub/id/userId from payload
 * 3) return null if not found
 */
function getUserIdFromStorageOrToken() {
  // 1) explicit userId in localStorage
  const ls = localStorage.getItem("userId");
  if (ls) {
    const n = Number(ls);
    if (!Number.isNaN(n)) return n;
  }

  // 2) try decode JWT from localStorage.token
  const token = localStorage.getItem("token");
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    // base64url decode payload
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // pad length
    const pad = payloadB64.length % 4;
    const padded = payloadB64 + (pad ? "=".repeat(4 - pad) : "");
    const json = atob(padded);
    const payload = JSON.parse(json);
    // common claim names: sub, id, userId
    if (payload.sub && !Number.isNaN(Number(payload.sub))) return Number(payload.sub);
    if (payload.id && !Number.isNaN(Number(payload.id))) return Number(payload.id);
    if (payload.userId && !Number.isNaN(Number(payload.userId))) return Number(payload.userId);
    // sometimes user is nested: payload.user?.id
    if (payload.user && (payload.user.id || payload.user.userId)) {
      const maybe = payload.user.id ?? payload.user.userId;
      if (!Number.isNaN(Number(maybe))) return Number(maybe);
    }
  } catch (e) {
    // decoding failed; just return null
    return null;
  }

  return null;
}

function fmtDate(dt) {
  try {
    return dt ? new Date(dt).toLocaleString() : "—";
  } catch {
    return dt;
  }
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);

  // derive userId from storage/token
  const userId = getUserIdFromStorageOrToken();

  useEffect(() => {
    mountedRef.current = true;
    loadNotifications();
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadNotifications() {
    setLoading(true);
    setError("");

    try {
      if (!userId) {
        throw new Error(
          `No user id available. Please either:
  1) set localStorage.userId to the current user's numeric id (example: localStorage.setItem('userId','123'))
  or
  2) ensure localStorage.token contains a JWT whose payload has sub, id, or userId claim.`
        );
      }

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token provided. Please log in.");
      }

      const res = await fetch(
        `${API_BASE}/api/users-notifications/user/${userId}`,
        {
          headers: apiHeaders(false),
        }
      );

      const ct = res.headers.get("content-type") || "";
      const body = ct.includes("application/json")
        ? await res.json().catch(() => null)
        : await res.text().catch(() => null);

      if (!res.ok) {
        throw new Error(body?.error || body || `Failed to load notifications (${res.status})`);
      }

      if (mountedRef.current) {
        setNotifications(Array.isArray(body) ? body : []);
      }
    } catch (err) {
      console.error("loadNotifications error:", err);
      if (mountedRef.current) setError(err.message || "Failed to load notifications");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // mark single notification as read
  async function markRead(id) {
    if (!id) return;
    setNotifLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/users-notifications/${id}/read`,
        {
          method: "PUT",
          headers: apiHeaders(false),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.warn("markRead server returned:", res.status, txt);
      }
      // optimistic/local update
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (err) {
      console.error("markRead error:", err);
      // local fallback
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } finally {
      setNotifLoading(false);
    }
  }

  // mark all read
  async function markAllRead() {
    if (!userId) {
      setError("No userId available to mark notifications as read.");
      return;
    }
    if (notifications.length === 0) return;

    setNotifLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/users-notifications/user/${userId}/read-all`,
        {
          method: "PUT",
          headers: apiHeaders(false),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.warn("markAllRead server returned:", res.status, txt);
      }
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      console.error("markAllRead error:", err);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } finally {
      setNotifLoading(false);
    }
  }

  return (
    <div className="notifications-pagee">
      <header className="notifications-header">
        <div>
          <h2>Notifications</h2>
          <p className="sub">System notifications for the current user</p>
        </div>

        <div className="notifications-controls">
          <div className="notif-summary">
            <strong>{unreadCount}</strong> unread
          </div>
          <button className="btn" onClick={loadNotifications} disabled={loading}>
            Refresh
          </button>
          <button
            className="btn primary"
            onClick={markAllRead}
            disabled={notifLoading || notifications.length === 0}
          >
            Mark all read
          </button>
        </div>
      </header>

      {loading ? (
        <div className="notif-loading">Loading…</div>
      ) : error ? (
        <div className="notif-error">Error: {error}</div>
      ) : (
        <section className="notif-list">
          {notifications.length === 0 ? (
            <div className="notif-empty">No notifications</div>
          ) : (
            <ul>
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={`notif-item ${n.is_read ? "read" : "unread"}`}
                >
                  <div className="notif-main">
                    <div className="notif-title">{n.type || "Notification"}</div>
                    <div className="notif-message">{n.message}</div>
                    <div className="notif-meta">{fmtDate(n.created_at)}</div>
                  </div>
                  <div className="notif-actions">
                    {!n.is_read && (
                      <button
                        className="btn small"
                        onClick={() => markRead(n.id)}
                        disabled={notifLoading}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="notif-footer-note">
        {/* <small>
          This page calls <code>/api/users-notifications/user/:userId</code> and the read endpoints.
          If you see the user id error, either set <code>localStorage.userId</code> to the current user's numeric id
          (example: <code>localStorage.setItem("userId","123")</code>) or ensure your <code>localStorage.token</code>
          is a JWT containing <code>sub</code>, <code>id</code> or <code>userId</code> in its payload.
        </small> */}
      </div>
    </div>
  );
}
